import type { CSSProperties } from 'react';
// Use the light/async build for smaller bundle
import { PrismAsyncLight as SyntaxHighlighter, PrismLight as SyntaxHighlighterSync } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import ruby from 'react-syntax-highlighter/dist/esm/languages/prism/ruby';
// Register only the languages we need
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import oneDarkBase from 'react-syntax-highlighter/dist/esm/styles/prism/one-dark';
import oneLightBase from 'react-syntax-highlighter/dist/esm/styles/prism/one-light';

SyntaxHighlighter.registerLanguage('sql', sql);
// Synchronous SQL highlighter — used as the live `highlight` for the SQL editor (an async
// highlighter would return nothing on the first tokenize and flash/mis-measure per keystroke).
SyntaxHighlighterSync.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('yml', yaml);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('rb', ruby);

// One shape for all four Prism variants: keep the base theme's token colors and override only the
// pre/code container props. Single source so the light/dark × preview/editor variants can't drift.
type PrismStyle = typeof oneLightBase;
function themed(base: PrismStyle, container: CSSProperties): PrismStyle {
  return {
    ...base,
    'pre[class*="language-"]': { ...base['pre[class*="language-"]'], ...container },
    'code[class*="language-"]': { ...base['code[class*="language-"]'], ...container },
  };
}

// Editor variant: keep the token *colors* but drop the container typography the theme injects (font,
// size, line-height, wrapping) so it inherits react-simple-code-editor's textarea metrics — identical
// metrics on both layers are what keep the caret aligned with the visible characters.
const editorNeutralize = {
  background: 'transparent',
  margin: 0,
  padding: 0,
  fontFamily: 'inherit',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  whiteSpace: 'inherit',
  wordBreak: 'inherit',
  overflowWrap: 'inherit',
} as const;

// Previews: transparent bg so the surrounding surface shows. `*Editor`: neutralized metrics.
const oneLight = themed(oneLightBase, { background: 'transparent' });
const oneDark = themed(oneDarkBase, { background: 'transparent' });
const oneLightEditor = themed(oneLightBase, editorNeutralize);
const oneDarkEditor = themed(oneDarkBase, editorNeutralize);

/** Shared `customStyle` for inline code blocks — a module-level const so it keeps a
 * stable reference across renders (an inline literal re-renders the highlighter). */
export const codeBlockCustomStyle = {
  margin: 0,
  padding: 0,
  fontSize: '0.75rem',
  lineHeight: '1.5',
  background: 'transparent',
} as const;

export { oneDark, oneDarkEditor, oneLight, oneLightEditor, SyntaxHighlighter, SyntaxHighlighterSync };
